import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ClipboardList, CheckCircle, XCircle } from "lucide-react";
import TopBar from "../components/TopBar";
import './css/ViewResponse.css';
import axios from "./../axios";

const ViewResponse = ({ setSideBar, setSettingDrawer }) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [response, setResponse] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("student_token");
        const testsResponse = await axios.get(`student/mcq/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setResponse(testsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setResponse(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (isLoading) {
    return (
      <>
        <TopBar title="View Response" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
        <div className="view-response-container p-4 max-w-4xl mx-auto">
          <div className="view-response-header flex items-center gap-2 mb-4">
            <div className="h-6 w-6 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="details-card bg-white shadow rounded-lg p-6">
            <div className="detail-field flex items-center gap-3 mb-4">
              <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
              <div className="flex-1">
                <div className="h-4 w-16 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="detail-field flex items-center gap-3 mb-4">
              <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
              <div className="flex-1">
                <div className="h-4 w-16 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="response-list">
              <div className="h-6 w-40 bg-gray-200 rounded mb-4 animate-pulse"></div>
              {[1, 2].map((i) => (
                <div key={i} className="response-item flex gap-3 mb-4 p-4 border rounded">
                  <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
                  <div className="flex-1">
                    <div className="h-5 w-3/4 bg-gray-200 rounded mb-2 animate-pulse"></div>
                    <div className="h-4 w-1/2 bg-gray-200 rounded mb-2 animate-pulse"></div>
                    <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!response || !response.test || response.test.length === 0) {
    return (
      <>
        <TopBar title="View Response" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
        <div className="view-response-container p-4 max-w-4xl mx-auto">
          <div className="view-response-header flex items-center gap-2 mb-4">
            <ClipboardList size={20} className="header-icon" />
            <h2>Response Not Found</h2>
          </div>
          <div className="details-card bg-white shadow rounded-lg p-6">
            <div className="no-response-message text-center text-gray-600">
              Sorry, the response for this test could not be found. Please check the test ID or return to the tests list.
            </div>
          </div>
        </div>
      </>
    );
  }

  const test = response.test[0];
  const questions = response.questions || [];
  
  // Calculate question counts
  const totalQuestions = questions.length;
  const correctAnswers = questions.filter(q => q.correct_option === q.selected_option).length;
  const incorrectAnswers = totalQuestions - correctAnswers;

  return (
    <>
      <TopBar title="View Response" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
      <div className="view-response-container p-4 max-w-4xl mx-auto">
        <div className="view-response-header flex items-center gap-2 mb-4">
          <ClipboardList size={20} className="header-icon" />
          <h2>{test.topic}</h2>
        </div>
        <div className="details-card bg-white shadow rounded-lg p-6">
          <div className="detail-field flex items-center gap-3 mb-4">
            <div className="field-icon">
              <ClipboardList size={18} />
            </div>
            <div className="field-content">
              <span className="field-label font-medium text-gray-600">Title</span>
              <span className="field-value text-gray-900">{test.topic}</span>
            </div>
          </div>
          <div className="detail-field flex items-center gap-3 mb-4">
            <div className="field-icon">
              <ClipboardList size={18} />
            </div>
            <div className="field-content">
              <span className="field-label font-medium text-gray-600">Details</span>
              <span className="field-value text-gray-900">{test.details}</span>
            </div>
          </div>
          <div className="score-summary p-3 mb-6 rounded-lg">
            <h3 className="text-md font-semibold mb-2 flex items-center gap-2">
                Summary
            </h3>
            <div className="summary-list flex items-center gap-4">
              <div className="summary-item flex items-center gap-1.5 px-3 py-1.5 rounded-md">
                <ClipboardList size={14} className="text-blue-600" />
                <span className="summary-label text-sm font-medium text-gray-600">Total:</span>
                <span className="summary-value text-sm font-semibold text-gray-900">{totalQuestions}</span>
              </div>
              <div className="summary-item flex items-center gap-1.5 px-3 py-1.5 rounded-md correct">
                <CheckCircle size={14} className="text-green-600" />
                <span className="summary-label text-sm font-medium text-gray-600">Correct:</span>
                <span className="summary-value text-sm font-semibold text-green-600">{correctAnswers}</span>
              </div>
              <div className="summary-item flex items-center gap-1.5 px-3 py-1.5 rounded-md incorrect">
                <XCircle size={14} className="text-red-600" />
                <span className="summary-label text-sm font-medium text-gray-600">Incorrect:</span>
                <span className="summary-value text-sm font-semibold text-red-600">{incorrectAnswers}</span>
              </div>
            </div>
          </div>
          <div className="response-list mt-6">
            <h3 className="response-list-header text-lg font-semibold mb-4">Question Responses</h3>
            {questions.length === 0 ? (
              <div className="text-center text-gray-600">No questions available for this test.</div>
            ) : (
              questions.map((question) => (
                <div
                  key={question.id}
                  className={`response-item flex gap-3 p-4 mb-4 border rounded ${
                    question.correct_option === question.selected_option
                      ? 'correct bg-green-50 border-green-200'
                      : 'incorrect bg-red-50 border-red-200'
                  }`}
                >
                  <div className="response-icon">
                    {question.correct_option === question.selected_option ? (
                      <CheckCircle size={18} className="text-green-600" />
                    ) : (
                      <XCircle size={18} className="text-red-600" />
                    )}
                  </div>
                  <div className="response-content flex-1">
                    <p className="question-text font-medium text-gray-900">{question.question}</p>
                    <p className="answer text-sm mt-2">
                      <span className="answer-label font-medium text-gray-600">Your Answer: </span>
                      <span
                        className={`text-gray-900 ${
                          question.correct_option === question.selected_option ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {question.selected}
                      </span>
                    </p>
                    <p className="answer text-sm">
                      <span className="answer-label font-medium text-gray-600">Correct Answer: </span>
                      <span className="text-green-600">{question.correct}</span>
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewResponse;